import React from "react";
import {
  formatRelativeTime,
  leadDisplayName,
  leadInitials,
  snippetPreview,
} from "./utils";

const TABS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "favorites", label: "Favorites" },
];

function Avatar({ lead }) {
  if (lead?.profileImageUrl) {
    return (
      <img
        src={lead.profileImageUrl}
        alt=""
        className="w-10 h-10 rounded-full object-cover"
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-semibold">
      {leadInitials(lead)}
    </div>
  );
}

export default function ConversationList({
  threads,
  selectedThreadId,
  tab,
  unreadTotal,
  search,
  loading,
  activeFilterCount,
  onTabChange,
  onSearchChange,
  onOpenFilters,
  onSelect,
  onLoadMore,
  hasMore,
}) {
  return (
    <div className="flex flex-col h-full border-r border-slate-200 bg-white">
      <div className="px-4 pt-4 pb-2">
        <h1 className="text-xl font-semibold text-slate-900">Unibox</h1>
        <div className="mt-3 flex gap-1 border-b border-slate-100">
          {TABS.map((item) => {
            const active = tab === item.id;
            const label =
              item.id === "unread" && unreadTotal > 0
                ? `Unread (${unreadTotal})`
                : item.label;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onTabChange(item.id)}
                className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                  active
                    ? "border-blue-500 text-blue-600 font-medium"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex gap-2">
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search"
            className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-400 focus:bg-white"
          />
          <button
            type="button"
            onClick={onOpenFilters}
            title="Filters"
            className={`relative flex-shrink-0 w-10 h-10 rounded-lg border flex items-center justify-center transition-colors ${
              activeFilterCount
                ? "border-blue-400 text-blue-600 bg-blue-50"
                : "border-slate-200 text-slate-500 hover:bg-slate-50"
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {activeFilterCount > 0 ? (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-blue-500 text-white text-[10px] font-semibold flex items-center justify-center">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && threads.length === 0 ? (
          <div className="p-4 text-sm text-slate-400">Loading conversations…</div>
        ) : threads.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">
            No conversations yet
          </div>
        ) : (
          threads.map((thread) => {
            const selected = thread.threadId === selectedThreadId;
            const unread = (thread.unreadCount || 0) > 0;
            return (
              <button
                key={thread.threadId}
                type="button"
                onClick={() => onSelect(thread)}
                className={`w-full text-left px-4 py-3 flex gap-3 border-b border-slate-50 transition-colors ${
                  selected ? "bg-blue-50" : "hover:bg-slate-50"
                }`}
              >
                <div className="relative flex-shrink-0">
                  <Avatar lead={thread.lead} />
                  <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#0A66C2] text-white text-[8px] flex items-center justify-center font-bold border border-white">
                    in
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`truncate text-sm ${
                        unread ? "font-semibold text-slate-900" : "font-medium text-slate-800"
                      }`}
                    >
                      {leadDisplayName(thread.lead)}
                    </span>
                    <span className="text-[11px] text-slate-400 flex-shrink-0">
                      {formatRelativeTime(thread.lastMessageAt)}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <p
                      className={`truncate text-xs ${
                        unread ? "text-slate-700" : "text-slate-500"
                      }`}
                    >
                      {snippetPreview(thread.lastMessage)}
                    </p>
                    {unread ? (
                      <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-semibold flex items-center justify-center">
                        {thread.unreadCount}
                      </span>
                    ) : null}
                    {thread.isFavorite ? (
                      <span className="text-amber-400 text-xs flex-shrink-0">★</span>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })
        )}
        {hasMore ? (
          <button
            type="button"
            onClick={onLoadMore}
            className="w-full py-3 text-xs text-blue-600 hover:bg-slate-50"
          >
            Load more
          </button>
        ) : null}
      </div>
    </div>
  );
}
